import { Injectable } from "@angular/core";
import { Subscription } from "rxjs";
import { filter, first } from "rxjs/operators";
import { ExamStatus } from "src/app/components/examMode/interfaces";
import { MimeTypesEnum } from "src/app/utils/enums";
import { blobToFile } from "src/app/utils/helpers/files.helprer";
import { AuthService } from "../auth/auth.service";
import { AutosaveManagerService } from "../autosave/autosave-manager.service";
import { IAutoSaveDoc } from "../autosave/autosave.interface";
import { FileHashManager } from "../autosave/file-hash-manager";
import { ExamTimerService } from "../restrictions/exam-timer.service";
import { ExamService } from "../restrictions/exam.service";
import { TabService } from "../tab.service";
import { CurrentExamService } from "./current-exam.service";
import { ExamDocumentType } from "./enums/document-type.enum";
import { ExamCommunicationService } from "./exam-communication.service";
import { ExamModeRestrictionsService } from "./exam-mode-restrictions.service";
import { ExamDocumentMetadata } from "./interfaces/exam-document-metadata.interface";
import { Exam } from "./interfaces/exam.interface";
import { PrepareExam } from "./interfaces/prepare-exam.interface";
import { UserExam } from "./interfaces/user-exam.interface";
import { PlatformDetectionService } from "../platform-detection.service";
import { MatDialog } from "@angular/material/dialog";
import { MessageDialogActionColor, MessageDialogComponent, MessageDialogData } from "src/app/components/dialogs/message-dialog/message-dialog.component";
import { getMimeTypeFromFile } from "src/app/core/common";

@Injectable({
    providedIn: 'root'
})
export class ExamModeService {
    private examStartedNotificationTimerId: number | undefined = undefined;
    private EXAM_STARTED_NOTIFICATION_INTERVAL: number = 1000 * 5 * 60; // 5 mins

    private waitForTabsToCloseSub: Subscription;

    constructor(
        private readonly authService: AuthService,
        private readonly examCommunicationService: ExamCommunicationService,
        private readonly examModeRestrictionsService: ExamModeRestrictionsService,
        private readonly examService: ExamService,
        private readonly currentExamService: CurrentExamService,
        private readonly timerService: ExamTimerService,
        private readonly tabService: TabService,
        private readonly autoSave: AutosaveManagerService,
        private readonly platform: PlatformDetectionService,
        private readonly dialog: MatDialog,
    ) {
        this.initSubscriptions();
    }

    public async startExam(asInterrupted: boolean = false) {
        const { examMetadata, initialDocument } = this.currentExamService;
        if (this.waitForTabsToCloseSub) {
            this.waitForTabsToCloseSub.unsubscribe();
        }

        if (examMetadata === undefined) {
            throw new Error("Error during starting exam");
        }

        if (!asInterrupted) {
            await this.examModeRestrictionsService.showExamInformation(examMetadata);
        }

        if (initialDocument === undefined) {
            throw new Error("Error during opening exam document");
        }

        this.examCommunicationService.examStartedResponse(examMetadata.id);
        this.currentExamService.setInProgress(true);

        if (this.examStartedNotificationTimerId !== undefined) {
            window.clearInterval(this.examStartedNotificationTimerId);
        }

        // clear autosaved files from previous exams
        await this.autoSave.deleteAllExamFiles();

        await this.examModeRestrictionsService.openExam(initialDocument);
        await this.examModeRestrictionsService.startExam(asInterrupted);
    }

    public async dispose() {
        await this.examModeRestrictionsService.terminateExamIfNeeded();
        this.timerService.stop();
        this.examCommunicationService.dispose();
    }

    private initSubscriptions() {
        this.examCommunicationService.prepareExamFired.subscribe((examData: PrepareExam) => this.prepareExamHandler(examData));

        this.examCommunicationService.startExamFired.subscribe(async (exam: Exam) => {
            const file = await this.getExamDocument(exam);

            if (!file) {
                throw new Error('Exam file is not found');
            }

            // check if exam is opened in a supported browser
            if (!this.platform.isBrowserSupported) {
                this.browserNotSupportedWarning();
                throw new Error("Browser is not supported");
            }

            this.currentExamService.setExamMetadata(exam);
            this.currentExamService.setExamInitialDocument(file);

            this.tryToStartExam();
        });

        this.examCommunicationService.stopExamFired.subscribe(async exam => {
            if (exam.examId === this.currentExamService.examMetadata?.id) {
                await this.stopAndSubmitExam();
                await this.authService.logout();
            }
        });

        this.examCommunicationService.reconnectFired.subscribe(async userExam => {
            const { exam } = userExam;

            // check if exam is opened in a supported browser
            if (!this.platform.isBrowserSupported) {
                this.browserNotSupportedWarning();
                throw new Error("Browser is not supported");
            }

            this.currentExamService.setExamMetadata(exam);
            this.currentExamService.setInProgress(exam.examStatus == ExamStatus.InProgress);

            const examFile = await this.recoverExamFile(userExam);
            const drafts = await this.recoverDrafts(userExam);

            this.currentExamService.setExamInitialDocument(examFile instanceof File ? examFile : examFile.originalFile!);

            await this.examModeRestrictionsService.openExam(examFile, drafts);
            await this.examModeRestrictionsService.startExam(true);
        });

        this.examCommunicationService.unlockChangedFired.subscribe(examName => this.notifyUnlockChange(examName));
    }

    private async tryToStartExam() {
        const { examMetadata, inProgress } = this.currentExamService;

        if (examMetadata === undefined) {
            throw new Error("Exam is not found");
        }

        if (this.otherDocumentsOpen()) {
            await this.examModeRestrictionsService.warnUsersExamStarted(examMetadata.name);
        }

        this.startExamWhenTabClosed();

        if (!inProgress) {
            this.scheduleExamStartedNotification(examMetadata.name);
        }
    }

    private otherDocumentsOpen(): boolean {
        let tabsOpen = true; // send the warning by default

        // it's up to the tabservice to set it to false
        const sub = this.tabService.getCurrentTabs().subscribe((tabs) => {
            tabsOpen = tabs.length > 0;
        });
        sub.unsubscribe();

        return tabsOpen;
    }

    private startExamWhenTabClosed() {
        this.waitForTabsToCloseSub = this.tabService.getCurrentTabs()
            .pipe(filter(tabs => !tabs || tabs.length === 0), first())
            .subscribe(() => this.startExam());
    }

    private async stopAndSubmitExam(): Promise<void> {
        await this.examModeRestrictionsService.warnUserExamTimeIsOver();
        await this.examService.submitExam();
        await this.examModeRestrictionsService.warnUserExamSubmitted();
        this.examModeRestrictionsService.stopExam();
        await this.autoSave.deleteAllSavedFiles();
    }

    private scheduleExamStartedNotification(examName: string) {
        this.examStartedNotificationTimerId = window.setInterval(
            () => this.examModeRestrictionsService.warnUsersExamStarted(examName),
            this.EXAM_STARTED_NOTIFICATION_INTERVAL
        );
    }

    private async prepareExamHandler(data: PrepareExam) {
        await this.examModeRestrictionsService.warnUsersExamWillStartSoon(data.Name, data.ExamStartDelay.toString());

        const file = await this.downloadExamDocument(data.Id, data.Document);
        await this.saveFileToDB(file);
    }

    private async notifyUnlockChange(examName: string) {
        await this.examModeRestrictionsService.warnUnlockCodeChange(examName);
    }

    public async getExamDocument(exam: Exam): Promise<File | undefined> {
        const examDocMetadata = this.getExamFromExamFiles(exam.examFiles);

        if (examDocMetadata) {
            const examFile = await this.getExamFileFromDB(examDocMetadata.name);
            let file: File | undefined = undefined;

            file = examFile?.originalFile ?? await this.downloadExamDocument(exam.id, examDocMetadata);

            return file;
        }
    }

    public async downloadExamDocument(examId: number, examDocumentMetadata: ExamDocumentMetadata, type: string | undefined = undefined): Promise<File> {
        const examBlob = await this.examService.downloadExamFile(examId, examDocumentMetadata.name, examDocumentMetadata.path);
        return blobToFile(examBlob, examDocumentMetadata.name, type ?? getMimeTypeFromFile(examDocumentMetadata.name));
    }

    private getExamFromExamFiles(files: ExamDocumentMetadata[]): ExamDocumentMetadata | undefined {
        return files.find(x => x.documentType == ExamDocumentType.Exam);
    }

    private async saveFileToDB(file: File): Promise<void> {
        const docHash = await FileHashManager.calculateFileHash(file);
        await this.autoSave.addOriginalFile(docHash, file, file.name, null, ExamDocumentType.Exam);
    }

    private async getExamFileFromDB(fileName: string): Promise<IAutoSaveDoc | undefined> {
        const examDocs = await this.autoSave.getUnsavedChanges(true);
        return examDocs.filter(x => x.fileName === fileName && x.examFileType === ExamDocumentType.Exam)[0];
    }

    private async getDraftsFromDB(): Promise<IAutoSaveDoc[] | undefined> {
        const examDocs = await this.autoSave.getUnsavedChanges(true);
        return examDocs.filter(x => x.examFileType === ExamDocumentType.Draft);
    }

    private async  recoverExamFile(userExam: UserExam): Promise<File | IAutoSaveDoc> {
        let examFile: File | IAutoSaveDoc | undefined = undefined;

        const { exam } = userExam;

        // check local db for unsaved changes first
        let examDocumentMetadata = exam.examFiles.filter(x => x.documentType === ExamDocumentType.Exam)[0];
        examFile = await this.getExamFileFromDB(examDocumentMetadata.name);

        // if has not unsaved changes
        if (!examFile) {
            // check if file was saved in OneDrive
            const cloudExamDocumentMetadata = userExam.studentDocuments.filter(x => x.documentType === ExamDocumentType.SubmitExam)[0];
            if (cloudExamDocumentMetadata) {
                // set the correct name 'Exam-...' not the name used in the cloud
                cloudExamDocumentMetadata.name = examDocumentMetadata.name;
                examFile = await this.downloadExamDocument(exam.id, cloudExamDocumentMetadata);
            }
        }

        if (!examFile) {
            examFile = await this.downloadExamDocument(exam.id, examDocumentMetadata);
        }

        return examFile;
    }

    private async recoverDrafts(userExam: UserExam): Promise<File[] | IAutoSaveDoc[] | undefined> {
        let drafts: File[] | IAutoSaveDoc[] | undefined = undefined;

        // check local db for unsaved changes first
        drafts = await this.getDraftsFromDB();

        // if has not unsaved changes
        if (!drafts || !drafts.length) {
            // check if files were saved in OneDrive
            const savedDraftsInfo = userExam.studentDocuments.filter(x => x.documentType === ExamDocumentType.SubmitDraft);
            
            for (let i = 0; i < savedDraftsInfo.length; i++) {
                const draft = await this.downloadExamDocument(userExam.exam.id, savedDraftsInfo[i], MimeTypesEnum.ApplicationRtf.toString());
                drafts = !!drafts ? [...(drafts as unknown as File[]), draft] : [draft];
            }
        }

        return drafts;
    }

    private browserNotSupportedWarning(): void {
        const data: MessageDialogData = {
            title: '',
            message: 'UNSUPPORTED_BROWSER.TOASTR_MESSAGE',
            actions: [
                {
                    title: 'UNSUPPORTED_BROWSER.OK',
                    color: MessageDialogActionColor.Primary,
                    handler: () => {}
                }
            ]
        }
        this.dialog.open(MessageDialogComponent, { data });
    }
}
